import { Money } from '@shared/lib/shopify/types';

export function renderMoney(money: Money | number | undefined, withCents = false): string {
  if (money === undefined) {
    return withCents ? '$0.00' : '$0';
  }
  let amount: number;
  if (isMoney(money)) {
    if (money.currencyCode !== 'USD' && parseFloat(money.amount) > 0) {
      throw new Error('Invalid currency, expected USD.');
    }
    amount = parseFloat(money.amount);
  } else {
    amount = money;
  }

  if (withCents) {
    return `${amount < 0 ? '-' : ''}$${Math.abs(amount).toFixed(2)}`;
  } else {
    return `${amount < 0 ? '-' : ''}$${Math.abs(amount).toLocaleString()}`;
  }
}

export function getMoneyRange(prices: Money[]): { min: number; max: number } {
  return {
    min: Math.min(...prices.map((price) => parseFloat(price.amount))),
    max: Math.max(...prices.map((price) => parseFloat(price.amount))),
  };
}

export function convert_to_monthly(price: Money): Money {
  const monthly = parseFloat(price.amount) / 12;
  return { amount: monthly.toFixed(2), currencyCode: price.currencyCode };
}

function isMoney(obj: any): obj is Money {
  return obj && typeof obj === 'object' && 'amount' in obj && 'currencyCode' in obj;
}

export function renderMoneyRange(
  priceRange:
    | {
        maxVariantPrice: Money;
        minVariantPrice: Money;
      }
    | { min: number; max: number }
): string {
  let min: number;
  let max: number;
  if ('maxVariantPrice' in priceRange) {
    const { minVariantPrice, maxVariantPrice } = priceRange;
    if (minVariantPrice.currencyCode !== 'USD' || maxVariantPrice.currencyCode !== 'USD') {
      throw new Error('Invalid currency, expected USD.');
    }
    min = parseFloat(minVariantPrice.amount);
    max = parseFloat(maxVariantPrice.amount);
  } else {
    min = priceRange.min;
    max = priceRange.max;
  }
  if (min === max) {
    return renderMoney(min);
  } else {
    return `${renderMoney(min)} - ${renderMoney(max)}`;
  }
}

export function parseMoneyFromJSON(json: string | undefined): Money | undefined {
  if (json === undefined) {
    return undefined;
  }
  try {
    const obj = JSON.parse(json);
    return {
      amount: obj.amount,
      currencyCode: obj.currency_code,
    };
  } catch (e) {
    return undefined;
  }
}

export function addMoney(a: Money | undefined, b: Money | undefined): Money {
  if (a === undefined && b === undefined) {
    return zeroMoney;
  }
  if (a === undefined) {
    return b!;
  }
  if (b === undefined) {
    return a!;
  }
  if (a.currencyCode !== b.currencyCode) {
    throw new Error('Cannot add money with different currency codes.');
  }
  return {
    amount: (parseFloat(a.amount) + parseFloat(b.amount)).toFixed(2),
    currencyCode: a.currencyCode,
  };
}

export function subtractMoney(a: Money | undefined, b: Money | undefined): Money {
  if (a === undefined && b === undefined) {
    return zeroMoney;
  }
  if (a === undefined) {
    return b!;
  }
  if (b === undefined) {
    return a!;
  }
  if (a.currencyCode !== b.currencyCode) {
    throw new Error('Cannot subtract money with different currency codes.');
  }
  return {
    amount: (parseFloat(a.amount) - parseFloat(b.amount)).toFixed(2),
    currencyCode: a.currencyCode,
  };
}

export function negateMoney(a: Money): Money {
  return {
    amount: (-parseFloat(a.amount)).toFixed(2),
    currencyCode: a.currencyCode,
  };
}

export function multiplyMoney(a: Money, b: number): Money {
  return {
    amount: (parseFloat(a.amount) * b).toFixed(2),
    currencyCode: a.currencyCode,
  };
}

export const zeroMoney: Money = { amount: '0.00', currencyCode: 'USD' };

export function moneyUsd(amount: number): Money {
  return { amount: amount.toFixed(2), currencyCode: 'USD' };
}
